import React from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Stack,
  Checkbox,
  FormGroup,
  FormControlLabel as MuiFCL,
  Alert,
} from "@mui/material";
import { C } from "../constants";
import { LAYOUT } from "../constants/colors";
import { FIELD_TONES } from "./FormControls";
import { buildWizardPages } from "../constants/wizardPages";

export { buildWizardPages };

/** Per-input include switches — new accounts start with optional tracks OFF */
export const DEFAULT_ENABLED_MODULES = {
  dsa: false,
  systemDesign: true,
  subjects: false,
  questions: false,
  instructions: true,
  assessment: true,
  studentProfile: true,
  assessmentBanks: true,
  team: true,
  timing: true,
  calendar: false,
  dtStage: true,
  problem: true,
};

export function mergeEnabledModules(saved) {
  return {
    ...DEFAULT_ENABLED_MODULES,
    ...(saved && typeof saved === "object" ? saved : {}),
  };
}

export const FEATURE_TOGGLES = [
  { key: "team", label: "Members", icon: "ti-users" },
  { key: "timing", label: "Timing", icon: "ti-clock" },
  { key: "calendar", label: "Calendar", icon: "ti-calendar" },
  { key: "dtStage", label: "DT Stage", icon: "ti-route" },
  { key: "dsa", label: "DSA Syllabus", icon: "ti-binary-tree" },
  { key: "systemDesign", label: "System Design", icon: "ti-topology-star" },
  { key: "subjects", label: "Subjects", icon: "ti-books" },
  { key: "questions", label: "Question Banks", icon: "ti-help-octagon" },
  { key: "assessment", label: "Assessment", icon: "ti-chart-bar" },
  { key: "studentProfile", label: "Capability · Interests · Domain", icon: "ti-user-check" },
  { key: "assessmentBanks", label: "Practice Banks", icon: "ti-stack-2" },
];

export function PageIncludeToggle({ label, enabled, onChange, required = false, compact = false }) {
  if (required) {
    return (
      <Typography variant="caption" sx={{ fontWeight: 600, color: "error.main", letterSpacing: "0.04em" }}>
        REQUIRED
      </Typography>
    );
  }
  return (
    <FormControlLabel
      sx={{
        m: 0,
        ".MuiFormControlLabel-label": { fontSize: 12, fontWeight: 500, color: "text.secondary" },
      }}
      control={
        <Switch
          size="small"
          checked={!!enabled}
          onChange={(e) => onChange(e.target.checked)}
          color="success"
        />
      }
      label={compact ? "Include" : `Include ${label}`}
      labelPlacement="start"
    />
  );
}

export function FormSection({ title, icon, children, action, muted, tone = "navy" }) {
  const t = FIELD_TONES[tone] || FIELD_TONES.navy;
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        overflow: "hidden",
        borderColor: C.border,
        borderRadius: "10px",
        bgcolor: C.surface,
        boxShadow: "none",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          background: "#F8FAFC",
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {icon && (
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: "6px",
                bgcolor: "#fff",
                border: `1px solid ${C.border}`,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: t.accent,
              }}
            >
              <i className={`ti ${icon}`} style={{ fontSize: 14 }} />
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {title}
          </Typography>
        </Stack>
        {action}
      </Stack>
      <Box sx={{ p: 1.75, opacity: muted ? 0.55 : 1, pointerEvents: muted ? "none" : "auto" }}>
        {muted ? (
          <Typography variant="body2" color="text.secondary">
            Turn Include back ON above to edit this section.
          </Typography>
        ) : children}
      </Box>
    </Paper>
  );
}

export function FeatureTogglePanel({ enabled, onChange }) {
  const onCount = FEATURE_TOGGLES.filter((t) => enabled[t.key] !== false).length;
  return (
    <Box>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={750} sx={{ letterSpacing: "-0.02em" }}>
          Modules to include
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: C.muted, fontVariantNumeric: "tabular-nums" }}>
          {onCount}/{FEATURE_TOGGLES.length} on
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.75, maxWidth: 520 }}>
        Uncheck anything you filled but don’t want in the schedule.
      </Typography>
      <FormGroup
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
          gap: 1,
        }}
      >
        {FEATURE_TOGGLES.map((t) => {
          const on = enabled[t.key] !== false;
          return (
            <Paper
              key={t.key}
              variant="outlined"
              sx={{
                px: 1.25,
                py: 0.35,
                borderRadius: `${LAYOUT.radiusSm}px`,
                borderColor: on ? `${C.primary}55` : C.border,
                bgcolor: on ? C.primarySoft : C.surface,
                transition: "border-color 160ms ease, background 160ms ease",
                "&:hover": { borderColor: on ? C.primary : C.borderStrong },
              }}
            >
              <MuiFCL
                control={
                  <Checkbox
                    size="small"
                    checked={on}
                    onChange={(e) => onChange(t.key, e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <i className={`ti ${t.icon}`} style={{ fontSize: 16, color: on ? C.primary : C.faint }} />
                    <Typography variant="body2" fontWeight={650} sx={{ color: on ? C.text : C.muted }}>
                      {t.label}
                    </Typography>
                  </Stack>
                }
              />
            </Paper>
          );
        })}
      </FormGroup>
    </Box>
  );
}

/**
 * Product stepper — numbered progress rail (1 — 2 — 3 …).
 * Single brand color, full-width track, no rainbow / chip styling.
 */
export function WizardStepper({ pages, pageId, onSelect, embedded = false }) {
  const idx = Math.max(0, pages.findIndex((p) => p.id === pageId));
  const total = pages.length;
  const progress = total <= 1 ? 1 : idx / (total - 1);

  return (
    <Box sx={{ width: "100%", px: embedded ? 0 : 0.5, py: 0 }}>
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        sx={{ mb: 0.35, gap: 1.5 }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "-0.02em",
            color: C.text,
          }}
        >
          {pages[idx]?.title || "Setup"}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: C.muted,
            fontVariantNumeric: "tabular-nums",
            flexShrink: 0,
          }}
        >
          Step {idx + 1} of {total}
        </Typography>
      </Stack>
      {pages[idx]?.hint ? (
        <Typography sx={{ fontSize: 11, color: C.muted, mb: 0.75, lineHeight: 1.3 }}>
          {pages[idx].hint}
          {pages[idx].optional ? " · Optional — skip if empty." : ""}
          {pages[idx].required ? " · Required." : ""}
        </Typography>
      ) : null}

      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: { xs: 40, sm: 44 },
          display: "flex",
          alignItems: "flex-start",
          pt: 0.25,
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: { xs: 10, sm: 12 },
            right: { xs: 10, sm: 12 },
            top: { xs: 11, sm: 12 },
            height: 1.5,
            borderRadius: 99,
            bgcolor: "#E2E8F0",
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: { xs: 10, sm: 12 },
            top: { xs: 11, sm: 12 },
            height: 1.5,
            borderRadius: 99,
            width: {
              xs: `calc((100% - 20px) * ${progress})`,
              sm: `calc((100% - 24px) * ${progress})`,
            },
            background: C.primary,
            transition: "width 0.35s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />

        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ position: "relative", zIndex: 1, width: "100%" }}
        >
          {pages.map((p, i) => {
            const done = i < idx;
            const active = i === idx;
            return (
              <Box
                key={p.id}
                component="button"
                type="button"
                aria-label={`${i + 1}. ${p.title}`}
                aria-current={active ? "step" : undefined}
                title={p.title}
                onClick={() => onSelect?.(p.id)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.25,
                  flex: "1 1 0",
                  minWidth: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  p: 0,
                  fontFamily: "inherit",
                  "&:focus-visible .step-dot": {
                    outline: "none",
                    boxShadow: `0 0 0 4px ${C.primarySoft}, 0 0 0 6px ${C.primary}55`,
                  },
                }}
              >
                <Box
                  className="step-dot"
                  sx={{
                    width: { xs: 20, sm: 22 },
                    height: { xs: 20, sm: 22 },
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 700,
                    transition: "border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease",
                    border: active
                      ? `2px solid ${C.primary}`
                      : done
                        ? `2px solid ${C.primary}`
                        : `1.5px solid ${C.borderStrong}`,
                    color: active || done ? "#fff" : C.muted,
                    background: active || done ? C.primary : C.surface,
                    boxShadow: active
                      ? `0 0 0 3px ${C.primarySoft}`
                      : "0 1px 2px rgba(10,14,23,0.04)",
                    ".MuiBox-root:hover > &": {
                      borderColor: C.primary,
                      color: active || done ? "#fff" : C.primary,
                      background: active || done ? C.primaryDark : C.primarySoft,
                      boxShadow: `0 0 0 4px ${C.primarySoft}`,
                    },
                  }}
                >
                  {done ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        lineHeight: 0,
                        "& i": { fontSize: { xs: 10, sm: 11 }, fontWeight: 700 },
                      }}
                    >
                      <i className="ti ti-check" />
                    </Box>
                  ) : (
                    i + 1
                  )}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: { xs: 8, sm: 9.5 },
                    fontWeight: active ? 800 : 650,
                    color: active ? C.primaryDark : done ? C.primary : C.muted,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.15,
                    textAlign: "center",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    px: 0.25,
                  }}
                >
                  {p.short || p.title}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

export function WizardNav(props) {
  return <WizardStepper {...props} />;
}

/** Vertical named input navigation (sidebar-style sub-nav for wizard pages). */
export function InputSideNav({ pages = [], pageId, onSelect, pageIncluded }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderRadius: `${LAYOUT.radius}px`,
        borderColor: C.border,
        bgcolor: C.surface,
        position: { md: "sticky" },
        top: { md: 88 },
        maxHeight: { md: "calc(100vh - 110px)" },
        overflowY: "auto",
      }}
    >
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.muted, letterSpacing: "0.06em", textTransform: "uppercase", px: 1, py: 0.75 }}>
        Inputs
      </Typography>
      <Stack spacing={0.35}>
        {pages.map((p) => {
          const active = p.id === pageId;
          const included = typeof pageIncluded === "function" ? pageIncluded(p.id) : true;
          const muted = p.toggleKey && included === false;
          return (
            <Box
              key={p.id}
              component="button"
              type="button"
              onClick={() => onSelect?.(p.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textAlign: "left",
                border: "none",
                cursor: "pointer",
                borderRadius: 1.5,
                px: 1.1,
                py: 0.9,
                fontFamily: "inherit",
                background: active ? C.primarySoft : "transparent",
                color: active ? C.primaryDark : muted ? C.faint : C.text,
                boxShadow: "none",
                "&:hover": {
                  background: active ? C.primarySoft : C.surfaceAlt,
                },
              }}
            >
              <Box
                component="i"
                className={p.icon || "ti-circle"}
                sx={{ fontSize: 15, color: active ? C.primary : C.muted, width: 18, textAlign: "center" }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 13, fontWeight: active ? 600 : 500, lineHeight: 1.2 }}>
                  {p.title}
                </Typography>
                {p.optional || p.required ? (
                  <Typography sx={{ fontSize: 10, color: C.muted, mt: 0.15 }}>
                    {p.required ? "Required" : muted ? "Excluded" : "Optional"}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

export function WizardFooter({
  canBack,
  canNext,
  nextLabel = "Next",
  backLabel = "Back",
  onBack,
  onNext,
  nextDisabledReason = "",
  showSkip = false,
  onSkip,
}) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={1.5}
      sx={{ mt: 2.5, pt: 2, borderTop: 1, borderColor: "divider" }}
    >
      <Button variant="outlined" color="inherit" disabled={!canBack} onClick={onBack}>
        {backLabel}
      </Button>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
        {nextDisabledReason ? (
          <Typography variant="caption" color="warning.main">{nextDisabledReason}</Typography>
        ) : null}
        {showSkip && (
          <Button variant="text" color="inherit" onClick={onSkip}>Skip</Button>
        )}
        <Button variant="contained" disabled={!canNext} onClick={onNext}>
          {nextLabel}
        </Button>
      </Stack>
    </Stack>
  );
}

export function WizardPageShell({ page, children, badge, includeToggle, headerRight }) {
  if (!page) return null;
  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        minHeight: 320,
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: `${LAYOUT.radius}px`,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: `1px solid ${C.border}`,
          background: "#F8FAFC",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: "8px", bgcolor: "#fff",
            border: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <i className={`ti ${page.icon || "ti-forms"}`} style={{ fontSize: 18, color: C.primary }} />
          </Box>
          <Box>
            <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap">
              <Typography variant="subtitle1" component="h2" sx={{ fontSize: 15, fontWeight: 600, m: 0 }}>
                {page.title}
              </Typography>
              {page.required && (
                <Typography variant="caption" color="error.main" fontWeight={750}>Required</Typography>
              )}
              {page.optional && (
                <Typography variant="caption" color="text.secondary" fontWeight={650}>Optional</Typography>
              )}
              {badge}
            </Stack>
            {page.hint && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, fontSize: 12.5 }}>
                {page.hint}
              </Typography>
            )}
          </Box>
        </Box>
        {headerRight}
      </Stack>
      <Box sx={{ px: 2.5, py: 2.25 }}>
        {includeToggle}
        {children}
      </Box>
    </Paper>
  );
}

export function WizardLayout({ children }) {
  return (
    <Box sx={{ width: "100%" }}>
      {children}
    </Box>
  );
}

/**
 * Sticky chrome: Inputs | Plans + professional step rail.
 * Stays under navbar; form / schedule content scrolls below.
 */
export function PlannerStickyHeader({
  view,
  onViewChange,
  planCount = 0,
  stepper = null,
}) {
  return (
    <Box
      sx={{
        position: "sticky",
        top: LAYOUT.navbarHeight,
        zIndex: 30,
        mb: 1.5,
        pt: 0.5,
        pb: 0.5,
        background: `linear-gradient(180deg, ${C.bg} 60%, rgba(238,242,247,0.92) 100%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: `${LAYOUT.radius}px`,
          bgcolor: C.surface,
          boxShadow: "0 1px 2px rgba(10,14,23,0.04), 0 8px 28px rgba(10,14,23,0.06)",
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: { xs: 1.25, md: 1.5 },
            py: 0.75,
            borderBottom: view === 0 && stepper ? `1px solid ${C.border}` : "none",
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            sx={{
              p: "3px",
              borderRadius: 1.5,
              bgcolor: C.surfaceAlt,
              border: `1px solid ${C.border}`,
            }}
          >
            {[
              { id: 0, label: "Inputs" },
              { id: 1, label: "Plans" },
            ].map((t) => {
              const active = view === t.id;
              return (
                <Button
                  key={t.id}
                  size="small"
                  onClick={() => onViewChange(t.id)}
                  sx={{
                    minWidth: 72,
                    px: 1.5,
                    py: 0.45,
                    borderRadius: 1,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    color: active ? C.text : C.muted,
                    background: active ? C.surface : "transparent",
                    boxShadow: active ? "0 1px 3px rgba(10,14,23,0.08)" : "none",
                    border: active ? `1px solid ${C.border}` : "1px solid transparent",
                    "&:hover": {
                      background: active ? C.surface : "rgba(255,255,255,0.6)",
                    },
                  }}
                >
                  {t.label}
                  {t.id === 1 && planCount > 0 ? (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        px: 0.75,
                        py: 0.1,
                        borderRadius: 99,
                        fontSize: 11,
                        fontWeight: 700,
                        bgcolor: active ? C.primarySoft : "#E8EDF5",
                        color: active ? C.primaryDark : C.muted,
                      }}
                    >
                      {planCount}
                    </Box>
                  ) : null}
                </Button>
              );
            })}
          </Stack>

          <Typography
            sx={{
              display: { xs: "none", sm: "block" },
              fontSize: 12,
              fontWeight: 600,
              color: C.faint,
              letterSpacing: "0.02em",
            }}
          >
            {view === 0 ? "Configure plan inputs" : "Generated schedules"}
          </Typography>
        </Stack>

        {view === 0 && stepper ? (
          <Box sx={{ px: { xs: 1.25, md: 1.75 }, py: 1 }}>
            {stepper}
          </Box>
        ) : null}
      </Paper>
    </Box>
  );
}
